﻿/**
 * Robust CSV parser that correctly handles:
 * - quoted values containing commas
 * - escaped double quotes ("")
 * - variable line endings (\r\n and \n)
 */
export function parseCsvText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const fields: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        fields.push(cur.trim());
        cur = '';
      } else {
        cur += char;
      }
    }
    fields.push(cur.trim());
    return fields;
  };

  const headers = parseLine(lines[0]);
  const rows: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const parsed = parseLine(lines[i]);
    if (parsed.some((p) => p.length > 0)) {
      rows.push(parsed);
    }
  }
  return { headers, rows };
}

export interface ColumnMapping {
  matric: number;
  name: number;
  email: number;
  password: number;
  level: number;
  gender: number;
  hall: number;
  department: number;
}

/**
 * Automatically inspects CSV headers and matches them to standard voter attributes.
 */
export function autoMatchColumns(headers: string[]): ColumnMapping {
  const cleanHeaders = headers.map((h) => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const mapping: ColumnMapping = {
    matric: -1,
    name: -1,
    email: -1,
    password: -1,
    level: -1,
    gender: -1,
    hall: -1,
    department: -1,
  };

  cleanHeaders.forEach((h, idx) => {
    if (
      mapping.matric === -1 &&
      (h.includes('matric') || h.includes('matno') || h.includes('regno') || h.includes('studentid') || h === 'id')
    ) {
      mapping.matric = idx;
    } else if (
      mapping.name === -1 &&
      (h.includes('fullname') || h.includes('studentname') || h.includes('votername') || h === 'name')
    ) {
      mapping.name = idx;
    } else if (mapping.email === -1 && (h.includes('email') || h.includes('mail'))) {
      mapping.email = idx;
    } else if (
      mapping.password === -1 &&
      (h.includes('pass') || h.includes('pwd') || h.includes('accesscode') || h.includes('pin') || h.includes('secret'))
    ) {
      mapping.password = idx;
    } else if (mapping.level === -1 && (h.includes('level') || h.includes('year') || h.includes('class') || h.includes('cohort'))) {
      mapping.level = idx;
    } else if (mapping.gender === -1 && (h.includes('gender') || h.includes('sex'))) {
      mapping.gender = idx;
    } else if (mapping.hall === -1 && (h.includes('hall') || h.includes('hostel') || h.includes('residence'))) {
      mapping.hall = idx;
    } else if (mapping.department === -1 && (h.includes('dept') || h.includes('department') || h.includes('faculty'))) {
      mapping.department = idx;
    }
  });

  // Fallbacks if not detected by naming
  if (mapping.matric === -1 && headers.length > 0) mapping.matric = 0;
  if (mapping.name === -1 && headers.length > 1) mapping.name = 1;

  return mapping;
}
